/**
 * Product page behaviour: variant picking, gallery, sticky buy bar.
 *
 * Loaded only on product pages. All of it is enhancement — without JS the page
 * still shows every image and the form still posts to /cart/add.
 */

/* =================================================================
 * <variant-picker>
 *
 * Variant data is embedded as JSON by Liquid, so choosing a size needs no
 * network round-trip: price, availability and the hidden id update instantly.
 * ================================================================= */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variant-data]').textContent);
    this.form = this.closest('product-form')?.querySelector('form');
    // Das versteckte id-Feld steht im Formular VOR dem Picker, nicht darin —
    // deshalb über das Formular suchen, nicht über `this`. Sonst bliebe es null
    // und der Warenkorb bekäme immer die zuerst gerenderte Variante.
    this.idInput = this.form?.querySelector('input[name="id"]');

    this.addEventListener('change', this.onChange);
  }

  onChange = () => {
    const selected = this.selectedOptions();
    const variant = this.variants.find((v) =>
      v.options.every((option, index) => option === selected[index])
    );

    this.update(variant);
    this.updateUrl(variant);
  };

  /**
   * Ein Wert je Option, in Positions-Reihenfolge (option1, option2, …) — passend
   * zur Reihenfolge von `variant.options`.
   *
   * Wichtig: auch versteckte Optionsfelder zählen mit. Einwertige Optionen (etwa
   * eine „Farbe“ mit nur einem Wert) rendert das Template als verstecktes Feld
   * statt als Knopf. Zählte man nur die angehakten Radios, fehlte dieser Wert und
   * der Abgleich mit `variant.options` verrutschte — keine Variante würde passen.
   */
  selectedOptions() {
    const byPosition = new Map();
    this.querySelectorAll('[name^="option"]').forEach((input) => {
      if (input.type === 'radio' && !input.checked) return;
      const position = Number(input.name.slice('option'.length));
      byPosition.set(position, input.value);
    });
    return [...byPosition.keys()]
      .sort((a, b) => a - b)
      .map((position) => byPosition.get(position));
  }

  update(variant) {
    const button = this.form?.querySelector('[type="submit"]');
    const label = button?.querySelector('[data-button-label]');
    const priceTarget = document.querySelector('[data-variant-price]');
    const availability = document.querySelector('[data-variant-availability]');

    if (!variant) {
      // The combination does not exist at all (rare, but possible with 2+ options).
      if (button) button.disabled = true;
      if (label) label.textContent = window.theme.strings.unavailable;
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;

    if (button) button.disabled = !variant.available;
    if (label) {
      label.textContent = variant.available
        ? window.theme.strings.addToCart
        : window.theme.strings.soldOut;
    }

    if (priceTarget && variant.price_html) {
      priceTarget.innerHTML = variant.price_html;
    }

    if (availability) {
      availability.hidden = !variant.available;
    }

    // Jump the gallery to the image belonging to the chosen variant.
    if (variant.featured_media_id) {
      document
        .querySelector('product-gallery')
        ?.showMedia(String(variant.featured_media_id));
    }
  }

  /**
   * Keep ?variant= in the URL in sync so the page can be shared, bookmarked and
   * reloaded on the same size — without adding a history entry per click.
   */
  updateUrl(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
}
customElements.define('variant-picker', VariantPicker);

/* =================================================================
 * <product-gallery>
 * ================================================================= */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-media-id]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb-for]')];

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.thumbFor));
    });

    // On mobile the gallery is a scroll-snap strip; keep the thumbs in step
    // with whatever the shopper swiped to.
    this.viewport = this.querySelector('[data-gallery-viewport]');
    if (this.viewport && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.markActive(entry.target.dataset.mediaId);
          });
        },
        { root: this.viewport, threshold: 0.6 }
      );
      this.slides.forEach((slide) => observer.observe(slide));
    }
  }

  showMedia(mediaId) {
    const slide = this.slides.find((s) => s.dataset.mediaId === mediaId);
    if (!slide) return;

    slide.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'start',
    });

    this.markActive(mediaId);
  }

  markActive(mediaId) {
    this.thumbs.forEach((thumb) => {
      thumb.setAttribute('aria-current', String(thumb.dataset.thumbFor === mediaId));
    });
  }
}
customElements.define('product-gallery', ProductGallery);

/* =================================================================
 * Product recommendations
 *
 * Fetched after load rather than rendered inline: the Recommendations API adds
 * latency, and nobody is waiting on cross-sells before they can read the price.
 * ================================================================= */

const recommendations = document.querySelector('[data-recommendations]');
if (recommendations?.dataset.url) {
  fetch(recommendations.dataset.url)
    .then((response) => response.text())
    .then((html) => {
      const fresh = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-recommendations]');

      // No recommendations yet (a new shop, or a product nobody has paired with
      // anything) — leave the placeholder empty rather than show a bare heading.
      if (fresh?.innerHTML.trim()) {
        recommendations.innerHTML = fresh.innerHTML;
        recommendations
          .querySelectorAll('[data-reveal]')
          .forEach((el) => el.classList.add('is-revealed'));
      }
    })
    .catch(() => {
      /* A failed cross-sell must never break the product page. */
    });
}

/* =================================================================
 * <sticky-buy>
 *
 * Sichtbar, sobald der echte Kaufbutton NICHT im Bild ist — auch schon beim
 * Einstieg, wenn er unterhalb des ersten Screens liegt. Der Knopf kauft
 * wirklich: Er schickt das Produktformular ab, statt nur hinzuscrollen und
 * einen zweiten Tipp zu verlangen. Nur wenn die gewählte Variante gerade
 * nicht kaufbar ist, scrollt er zum Formular.
 * ================================================================= */

class StickyBuy extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById('product-form');
    this.atc = this.form?.querySelector('[type="submit"]');
    this.label = this.querySelector('[data-button-label]');
    if (!this.atc || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => this.classList.toggle('is-visible', !entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(this.atc);

    this.querySelector('[data-sticky-buy-action]')?.addEventListener('click', this.onClick);

    // Preis der gewählten Variante mitführen (Größenwechsel ändert ihn ggf.).
    document.querySelector('variant-picker')?.addEventListener('change', () => {
      requestAnimationFrame(() => {
        const price = document.querySelector('[data-variant-price] .price__now');
        const target = this.querySelector('[data-sticky-price]');
        if (price && target) target.textContent = price.textContent.trim();
      });
    });
  }

  onClick = (event) => {
    event.preventDefault();

    if (this.atc.disabled) {
      this.atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Das Formular-Element <product-form> übernimmt Ajax, Fehler und Drawer.
    // Die Rückmeldung („Hinzugefügt“) spiegeln wir hier, damit man sie sieht,
    // obwohl der echte Button außerhalb des Bildschirms liegt.
    const original = this.label?.textContent;
    this.form.requestSubmit();
    if (this.label && window.theme?.strings?.added) {
      this.label.textContent = window.theme.strings.added;
      setTimeout(() => (this.label.textContent = original), 1500);
    }
  };
}
customElements.define('sticky-buy', StickyBuy);
